/* eslint-disable react/prop-types */
import React, { useMemo, useState, useEffect } from 'react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import merge from 'lodash/merge';
import throttle from 'lodash/throttle';

import { designTokens } from '@vmos2/design-system';
import { useExplain } from '@vmos2/utils';
import { defaultTheme } from './default.theme';
import { GlobalStyles } from './GlobalStyles';
import { getTheme } from './getTheme';
import { useThemeEditor } from './useThemeEditor';
import { useAccessibilityContext } from '@vmos2/providers';

export const findCurrentBreakpoint = () => {
  const media = designTokens.breakpoint;
  let breakpoint;

  Object.values(media).some(mediaQuery => {
    const matchMedia = window.matchMedia(`(${mediaQuery})`);

    if (matchMedia.matches) {
      breakpoint = Object.keys(media).find(key => {
        return `(${media[key]})` === matchMedia.media;
      });
    }

    return matchMedia.matches;
  });

  return breakpoint;
};

const mergeTheme = theme => merge({}, theme.default, theme[findCurrentBreakpoint()]);

const ThemeBreakpoints = ({ theme, children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => mergeTheme(theme));
  const explainedTheme = useExplain(currentTheme);

  useEffect(() => {
    const onChange = () => {
      setCurrentTheme(explainedTheme || mergeTheme(theme));
    };

    const handler = throttle(onChange, 400);

    window.addEventListener('resize', handler);
    onChange();

    return () => {
      window.removeEventListener('resize', handler);
    };
  }, [theme, explainedTheme]);

  useEffect(() => {
    if (theme.default) {
      const link = document.querySelector("link[rel*='icon']") || document.createElement('link');

      link.type = 'image/x-icon';
      link.rel = 'shortcut icon';

      if (Array.isArray(theme.default.favicon)) {
        theme.default.favicon.forEach(favicon => {
          link.href = favicon;
          document.getElementsByTagName('head')[0].appendChild(link);
        });
      } else {
        link.href = theme.default.favicon;
        document.getElementsByTagName('head')[0].appendChild(link);
      }
    }
  }, [theme]);

  if (!currentTheme) return null;

  return <EmotionThemeProvider theme={currentTheme}>{children}</EmotionThemeProvider>;
};

export const ThemeProvider = ({ theme, tenant, store, brandTheme, isKiosk, children }) => {
  const { isHighContrast } = useAccessibilityContext();

  const themeEditor = useMemo(
    () => ({
      tenant: theme || tenant.theme,
      store: store?.theme || {},
      default: defaultTheme,
      brand: brandTheme || {},
    }),
    [tenant.theme, theme, brandTheme, store?.theme]
  );

  const themes = useThemeEditor(themeEditor);

  const configuredTheme = useMemo(
    () => getTheme(themes.tenant, themes.store, themes.brand, isHighContrast),
    [themes, isHighContrast]
  );

  return (
    <>
      <GlobalStyles theme={configuredTheme} isHighContrast={isHighContrast} />
      <ThemeBreakpoints theme={configuredTheme} isKiosk={isKiosk}>
        {children}
      </ThemeBreakpoints>
    </>
  );
};

ThemeProvider.defaultProps = {
  configuredTheme: defaultTheme,
};
